import moment from 'moment'
import { TDay } from '@src/ui/components/Admin/Shifts/ShiftEditDuplicate/utils/types'
import { getFinalDay } from '@src/ui/components/Admin/Shifts/ShiftEditDuplicate/utils'

// Map string weekdays to moment.js day numbers (0-6, where 0 is Sunday)
export const WEEKDAY_MAP: { [key: string]: number } = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
}

interface IGenerateRecurringDaysProps {
  dayStart: Date | undefined
  dayEnd: Date | undefined
  hourStart: string
  hourEnd: string
  shiftBreak: number
  payValue: string
  days: TDay[]
  dateIds: { [key: string]: TDay }
}

/**
 * Generates day objects for recurring shifts based on selected date range
 * Generates ALL days in the range with proper working/non-working status
 *
 * @param dayStart Start day of the shift
 * @param dayEnd End day of the shift
 * @param hourStart Start hour of the shift
 * @param hourEnd End hour of the shift
 * @param shiftBreak break of the shift in minutes
 * @param payValue Pay value of the shift
 * @param days Days array
 * @param dateIds Date IDs map
 */
export const generateRecurringDays = ({
  dayStart,
  dayEnd,
  hourStart,
  hourEnd,
  shiftBreak,
  payValue,
  days,
  dateIds,
}: IGenerateRecurringDaysProps) => {
  const numberOfDays = moment(dayEnd).diff(moment(dayStart), 'days')
  const newDays: TDay[] = []

  // Create a map of dirty days by date_start to preserve only user modifications and backend data
  const dirtyDaysMap = new Map<string, TDay>()
  days.forEach((day: TDay) => {
    if (day.is_dirty) {
      dirtyDaysMap.set(day.date_start, day)
    }
  })

  /**
   * We generate the days in the inclusive range [startDay, endDay]
   */
  for (let i = 0; i <= numberOfDays; i++) {
    const currentDayStart = moment(dayStart).clone().add(i, 'days')
    const currentDayEnd = getFinalDay(currentDayStart, hourStart, hourEnd)
    const date_start = currentDayStart.format('MM/DD/YYYY')
    const date_end = currentDayEnd.format('MM/DD/YYYY')

    let day: TDay = {
      id: i,
      day_start: currentDayStart,
      day_end: currentDayEnd,
      date_start,
      date_end,
      hour_start: hourStart,
      hour_end: hourEnd,
      break: shiftBreak,
      pay_value: payValue,
      working: true,
      punch_cards_count: 0,
      existing: false,
      starting_day: false,
      is_dirty: false,
    }

    // Check if we have a dirty day with modifications to preserve
    const dirtyDay = dirtyDaysMap.get(date_start)
    if (dirtyDay) {
      day = {
        ...dirtyDay,
        existing: dirtyDay.existing || false,
        is_dirty: true, // Preserve the dirty day's properties, especially working status and ID
      }
    } else if (dateIds[date_start]) {
      // Fall back to date_ids if no dirty day (this is for existing backend data)
      day = {
        ...day,
        id: dateIds[date_start].id,
        working: dateIds[date_start].working,
        punch_cards_count: dateIds[date_start].punch_cards_count,
        existing: true,
        starting_day: false,
        is_dirty: true, // Backend data should be considered dirty to preserve it
      }
    }

    newDays.push(day)
  }

  return newDays
}
